import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import cardStyle from './Card.module.scss'
import cartStyle from './Cart.module.scss'
import productStyle from './Product.module.scss'
import style from './Skeleton.module.scss'

interface ICardsSkeletonProps {
  count: number
  // The grid's own column settings, so each outline takes the slot its card will take.
  perRow: number | null
  perRowMobile: number | null
}

// Standart's grid loader: one card outline per product — photo, text and the add button.
export const CardsSkeleton = ({ count, perRow, perRowMobile }: ICardsSkeletonProps) => {
  const { t } = useSurveyLocale()
  const viewport = useViewport()
  const sizeClass = viewport.mobile
    ? `${cardStyle.cardMobile} ${2 === perRowMobile ? cardStyle.cardMobile2PerRow : ''}`
    : null !== perRow
      ? cardStyle[`card${perRow}PerRow`] || ''
      : ''

  return (
    <div
      className={style.cards}
      role="status"
      aria-label={t('questions', 'ec', 'loading')}
      data-testid="SW-EC-GridSkeleton"
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className={`${cardStyle.card} ${sizeClass}`}>
          <div className={cardStyle.image}>
            <div className={style.photo} />
          </div>
          <div className={style.text}>
            <div className={style.bar} />
            <div className={`${style.bar} ${style.medium}`} />
            <div className={`${style.bar} ${style.short}`} />
          </div>
          <div className={style.button} />
        </div>
      ))}
    </div>
  )
}

interface IProductSkeletonProps {
  // How many thumbs the desktop column draws; none when the product has a single asset.
  thumbs: number
}

// Standart's detail-page loader: the thumb column, the main photo, and the column with the text and buttons.
export const ProductSkeleton = ({ thumbs }: IProductSkeletonProps) => {
  const { t } = useSurveyLocale()
  const viewport = useViewport()

  return (
    <section
      className={`${productStyle.product} ${viewport.mobile ? productStyle.productMobile : ''}`}
      role="status"
      aria-label={t('questions', 'ec', 'loading')}
      data-testid="SW-EC-PDPSkeleton"
    >
      {viewport.mobile ? (
        <div className={`${style.mainPhoto} ${style.mainPhotoMobile}`} />
      ) : (
        <div className={style.images}>
          {thumbs > 0 && (
            <div className={style.thumbs}>
              {Array.from({ length: thumbs }, (_, index) => (
                <div key={index} className={style.thumb} />
              ))}
            </div>
          )}
          <div className={style.mainPhoto} />
        </div>
      )}
      <div className={`${style.details} ${viewport.mobile ? style.detailsMobile : ''}`}>
        <div className={`${style.bar} ${style.title}`} />
        <div className={`${style.bar} ${style.title} ${style.medium}`} />
        <div className={`${style.bar} ${style.price}`} />
        <div className={style.cta} />
        <div className={style.cta} />
        <div className={style.bar} />
        <div className={`${style.bar} ${style.medium}`} />
        <div className={`${style.bar} ${style.short}`} />
      </div>
    </section>
  )
}

interface ILinesSkeletonProps {
  count: number
}

// Standart's cart loader: one outline per cart line — the photo with the add button under it, and the text.
export const LinesSkeleton = ({ count }: ILinesSkeletonProps) => {
  const { t } = useSurveyLocale()
  const viewport = useViewport()

  return (
    <div role="status" aria-label={t('questions', 'ec', 'loading')} data-testid="SW-EC-CartSkeleton">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className={cartStyle.product}>
          <div className={cartStyle.content}>
            <div className={cartStyle.imageWrapper}>
              <div className={`${style.lineThumb} ${viewport.mobile ? style.lineThumbMobile : ''}`} />
              <div className={`${style.button} ${style.lineButton}`} />
            </div>
            <div className={`${cartStyle.info} ${style.lineText}`}>
              <div className={`${style.bar} ${style.title}`} />
              <div className={`${style.bar} ${style.price}`} />
              <div className={`${style.bar} ${style.medium}`} />
              <div className={`${style.bar} ${style.short}`} />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
